/**
 * 이미지 미리보기 모달 (공통)
 *
 * 점검(Checklist) 탭과 계측(Measure) 탭에서 공통으로 사용하는
 * 풀스크린 이미지 프리뷰 모달
 *
 * 기능:
 * - 풀스크린 이미지 표시 (bg-black/95)
 * - 라벨 뱃지 (상단 중앙)
 * - 닫기 버튼 (Glass 스타일)
 * - 재촬영 버튼 (블루 그라데이션, optional)
 * - ESC 키, 배경 클릭으로 닫기
 * - createPortal로 document.body에 렌더링
 * - 로컬/서버 이미지 URL 변환 (Capacitor.convertFileSrc)
 */

import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { Camera } from "lucide-react";

/**
 * 이미지 URL 변환 (로컬/서버 URL 지원)
 * - 로컬 파일 경로 (file://, capacitor://) → Capacitor.convertFileSrc()로 변환
 * - 서버 URL → 그대로 반환
 */
export function getImageSrc(url: string | undefined): string | undefined {
  if (!url) return undefined;

  if (url.startsWith("file://") || url.startsWith("capacitor://")) {
    return Capacitor.convertFileSrc(url);
  }

  return url;
}

export interface ImagePreviewModalProps {
  isOpen: boolean;
  imageUrl: string;
  label: string;
  onClose: () => void;
  /** 재촬영 콜백 (전달하지 않으면 재촬영 버튼 숨김) */
  onRetake?: () => void;
}

export function ImagePreviewModal({
  isOpen,
  imageUrl,
  label,
  onClose,
  onRetake,
}: ImagePreviewModalProps) {
  // ESC 키 핸들러
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // 뒤로가기 이벤트 핸들러 (Android 백버튼 / 헤더 뒤로가기)
  useEffect(() => {
    if (!isOpen) return;
    const handleCloseTopModal = (e: Event) => {
      e.preventDefault(); // 이벤트 처리됨을 알림
      onClose();
    };
    document.addEventListener("closeTopModal", handleCloseTopModal);
    return () => document.removeEventListener("closeTopModal", handleCloseTopModal);
  }, [isOpen, onClose]);

  const displayImageUrl = useMemo(() => getImageSrc(imageUrl), [imageUrl]);

  if (!isOpen) return null;

  return createPortal(
    <div
      data-testid="image-preview-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`${label} 미리보기`}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/95"
      onClick={onClose}
    >
      {/* 라벨 - 상단 중앙 */}
      <div className="absolute top-0 left-0 right-0 pt-safe">
        <div className="flex justify-center pt-4">
          <span className="rounded-full bg-white/10 backdrop-blur-sm px-4 py-1.5 text-sm font-medium text-white/90">
            {label}
          </span>
        </div>
      </div>

      {/* 이미지 */}
      <img
        src={displayImageUrl}
        alt={`${label} 미리보기`}
        className="max-h-[70vh] max-w-[90vw] object-contain rounded-lg"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      />

      {/* 하단 액션 버튼들 */}
      <div
        className="absolute bottom-0 left-0 right-0 pb-safe"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div className="flex justify-center gap-3 pb-8 px-6">
          {/* 닫기 버튼 - Glass 스타일 */}
          <button
            type="button"
            onClick={onClose}
            className="flex-1 max-w-[160px] flex items-center justify-center gap-2 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3.5 text-sm font-semibold text-white active:scale-[0.98] active:bg-white/20 transition-all duration-150"
            aria-label="닫기"
          >
            <span className="text-lg">×</span>
            닫기
          </button>

          {/* 재촬영 버튼 - 블루 그라데이션 (onRetake가 있을 때만 표시) */}
          {onRetake && (
            <button
              type="button"
              onClick={onRetake}
              className="flex-1 max-w-[160px] flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 active:scale-[0.98] active:from-blue-600 active:to-blue-700 transition-all duration-150"
              aria-label="재촬영"
            >
              <Camera className="w-4 h-4" />
              재촬영
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
